// app/src/lib/cashflow/retainedBalance.js — the retained-earnings pot.
//
// The owner takes money out in two ways, and they are NOT the same event:
//
//   W2 draw       — paid through regular payroll. It is a draw against the
//                   month's own earnings (the reason for running it through
//                   payroll is tax timing: withholding lands bi-weekly instead
//                   of quarterly). It reduces retained INCOME, and the Cash
//                   flow view already treats it that way.
//
//   Retained draw — a pure cash distribution that flushes profit the business
//                   has already earned and kept. It is NOT new income, and it
//                   is NOT an expense. It reduces the retained BALANCE and
//                   nothing else.
//
// So the pot is:
//
//     balance(t) = Σ retained income up to t  −  Σ retained draws up to t
//
// It runs all-time and never resets at year end: profit kept in 2025 is still
// there to be drawn in 2026. That is what makes "flush the retained money" a
// single meaningful number rather than a per-year fiction.
//
// Pure functions — no db, no auth, no React. Cents throughout.

/** 'YYYY-MM' for a Date or an ISO date string. */
export function ymOf(d) {
  const s = typeof d === 'string' ? d : d.toISOString().slice(0, 10);
  return s.slice(0, 7);
}

/** Quarter (1-4) containing a 'YYYY-MM'. */
export function quarterOf(ym) {
  return Math.floor((Number(ym.slice(5, 7)) - 1) / 3) + 1;
}

const ymAdd = (ym, n) => {
  const y = Number(ym.slice(0, 4));
  const m = Number(ym.slice(5, 7)) - 1 + n;
  return `${y + Math.floor(m / 12)}-${String((m % 12 + 12) % 12 + 1).padStart(2, '0')}`;
};

/**
 * Running balance, month by month, oldest first.
 *
 * @param {Array<{ym:string, retainedIncomeCents?:number, retainedDrawCents?:number}>} months
 *        need not be sorted or gap-free; months are taken as given.
 * @returns {Array<{ym, retainedIncomeCents, retainedDrawCents, closingCents}>}
 */
export function buildLedger(months = []) {
  const rows = [...months].sort((a, b) => (a.ym < b.ym ? -1 : a.ym > b.ym ? 1 : 0));
  let running = 0;
  return rows.map((r) => {
    const income = r.retainedIncomeCents || 0;
    const drawn = r.retainedDrawCents || 0;
    running += income - drawn;
    return { ym: r.ym, retainedIncomeCents: income, retainedDrawCents: drawn, closingCents: running };
  });
}

/**
 * The pot over one window, as an account statement: what it opened at, what the
 * business added, what the owner took, what it closed at.
 *
 * `from`/`to` are inclusive 'YYYY-MM'. The opening balance is everything BEFORE
 * `from` — which is what makes this an all-time pot viewed through a window
 * rather than a figure that restarts.
 *
 * @param {Array} ledger  output of buildLedger
 * @param {{from:string, to:string}} window
 */
export function windowView(ledger, { from, to }) {
  let openingCents = 0, retainedInCents = 0, retainedDrawsCents = 0;
  for (const r of ledger) {
    if (r.ym < from) openingCents += r.retainedIncomeCents - r.retainedDrawCents;
    else if (r.ym <= to) {
      retainedInCents += r.retainedIncomeCents;
      retainedDrawsCents += r.retainedDrawCents;
    }
  }
  return {
    from, to,
    openingCents,
    retainedInCents,
    retainedDrawsCents,
    closingCents: openingCents + retainedInCents - retainedDrawsCents,
  };
}

/**
 * The three windows the Cash flow screen offers, given today.
 *   ytd         — Jan of this year → this month
 *   lastQuarter — the previous COMPLETE calendar quarter
 *   lastYear    — the previous calendar year, Jan → Dec
 *
 * Each is a window onto the same all-time pot, so `lastYear.closingCents` is the
 * balance as it stood on 31 Dec, not last year's profit in isolation.
 *
 * @param {string} todayIso 'YYYY-MM-DD'
 */
export function windowsFor(todayIso) {
  const ym = todayIso.slice(0, 7);
  const year = Number(ym.slice(0, 4));
  const q = quarterOf(ym);
  // Previous quarter, rolling back across the year boundary.
  const pq = q === 1 ? { year: year - 1, q: 4 } : { year, q: q - 1 };
  const qStart = `${pq.year}-${String((pq.q - 1) * 3 + 1).padStart(2, '0')}`;
  return {
    ytd: { from: `${year}-01`, to: ym },
    lastQuarter: { from: qStart, to: ymAdd(qStart, 2) },
    lastYear: { from: `${year - 1}-01`, to: `${year - 1}-12` },
  };
}

/**
 * Is this draw a candidate for "retained draw" rather than W2?
 *
 * A suggestion only — the operator's marking is the truth. The tell is the one
 * the existing reserve heuristic already uses: payroll is a STANDING amount that
 * repeats, so a draw whose size never recurs is the lump that flushes the pot.
 * Surfaced so the classify sheet can pre-tick it, never applied on its own.
 *
 * @param {{amountCents:number}} tx
 * @param {Array<{amountCents:number, date:string}>} allDraws  the full draw history
 */
export function looksLikeRetainedDraw(tx, allDraws = []) {
  const amt = Math.abs(tx?.amountCents || 0);
  if (!amt) return false;
  const near = (a, b) => Math.abs(a - b) <= Math.max(100, Math.min(a, b) * 0.01);
  const months = new Set();
  for (const d of allDraws) {
    if (near(Math.abs(d.amountCents || 0), amt)) months.add((d.date || '').slice(0, 7));
  }
  return months.size < 2; // never repeated in a second month → a one-off lump
}

// ── Live view ──────────────────────────────────────────────────────────────
// Assembled from monthlySummary(), which already owns the hard parts: financial
// -month attribution of deposits, card charges counted on the day charged, card
// bill payments dropped, clawbacks netted, 401(k) treated as money to the owner.
// Re-deriving any of that here would be a second source of truth.

/**
 * The retained pot, all-time, plus the three windows the Cash flow screen shows.
 *
 * `retainedIncomeCents` per month is monthlySummary's own `retainedCents`
 * (earnings − draws), so a W2 paycheck already reduces it — which is right: it
 * was paid out of that month's earnings. Confirmed retained draws are held apart
 * and come off the pot instead.
 *
 * @param {{ months?: number, today?: string }} [opts] months = how far back to
 *        reach; the default spans several years so the pot is genuinely all-time
 *        for a young business.
 */
export async function retainedView({ months = 120, today } = {}) {
  const k = typeof window !== 'undefined' ? window.kanjo : null;
  const summary = await k?.cashflow?.ownerDraw?.monthlySummary?.({ months });
  const series = (summary && summary.series) || [];
  const ledger = buildLedger(series.map((r) => ({
    ym: r.ym,
    retainedIncomeCents: r.retainedCents || 0,
    retainedDrawCents: r.retainedDrawCents || 0,
  })));
  const iso = today || (k?.ocr?.todayISO?.() ?? new Date().toISOString().slice(0, 10));
  const w = windowsFor(iso);
  const year = Number(iso.slice(0, 4));
  // Last year's CLOSING balance. The owner's confirmed figure wins: a bank feed
  // often does not reach back far enough to derive it, and they know it.
  let priorYearEndCents = null;
  try {
    const overrides = await k?.cashflow?.ownerDraw?.getYearEndOverrides?.();
    const entered = overrides && overrides[String(year - 1)];
    if (entered != null) priorYearEndCents = entered;
    else {
      const jan = series.find((r) => r.ym === `${year}-01`);
      if (jan && jan.yebCents != null) priorYearEndCents = jan.yebCents;
    }
  } catch { /* leave unknown */ }
  return {
    year,
    priorYearEndCents,
    byYear: yearView({ series, year, priorYearEndCents }),
    ledger,
    balanceCents: ledger.length ? ledger[ledger.length - 1].closingCents : 0,
    windows: {
      ytd: windowView(ledger, w.ytd),
      lastQuarter: windowView(ledger, w.lastQuarter),
      lastYear: windowView(ledger, w.lastYear),
    },
  };
}

/**
 * The pot as the owner reasons about it: one year at a time, with LAST year's
 * closing balance carried as its own line rather than folded into an opening
 * figure.
 *
 * Why not an opening balance: on 1 January this year's retained income is zero
 * by definition — there has been no trading. Showing "opening $0" every January
 * says nothing. What actually carries over is last year's CLOSING balance, and
 * it behaves differently: it was already income in the year it was earned, it
 * is drawn against separately, and it is the figure the owner usually knows by
 * hand when the bank feed does not reach back far enough.
 *
 * `priorYearEndCents` is therefore an INPUT, not something derived here — the
 * owner's confirmed year-end balance when they have set one, otherwise whatever
 * the books could derive.
 *
 * @param {object} opts
 * @param {Array} opts.series          monthlySummary rows (needs ym, retainedCents,
 *                                     retainedDrawCents, retainedPriorDrawCents)
 * @param {number} opts.year           the year being reported
 * @param {number|null} opts.priorYearEndCents  last year's closing balance
 */
export function yearView({ series = [], year, priorYearEndCents = null }) {
  const inYear = series.filter((r) => Number(r.ym.slice(0, 4)) === year);
  const sum = (f) => inYear.reduce((s, r) => s + (f(r) || 0), 0);

  const retainedIncomeCents = sum((r) => r.retainedCents);
  const allDraws = sum((r) => r.retainedDrawCents);
  // Draws against LAST year's balance, from BOTH routes:
  //   · retainedPriorDrawCents — the owner classified it "Retained — last year";
  //   · equityDrawCents        — the auto waterfall charged it to the prior-year
  //     reserve because it exceeded what the year had earned.
  // They are disjoint by construction: a classified draw is held out of the
  // buckets that feed the waterfall (ownerDraw.js), so it can never appear in
  // both. Counting only the first left an unclassified lump reducing the
  // Earnings table's "drawn from prior-year retained earnings" line while this
  // tile still reported the balance as untouched — two contradicting figures on
  // one screen, and the wrong one is the one used to decide what can be taken.
  const priorDrawsCents = sum((r) => r.retainedPriorDrawCents) + sum((r) => r.equityDrawCents);
  // Everything else came out of what this year has kept so far.
  const thisYearDrawsCents = allDraws - priorDrawsCents;

  const known = priorYearEndCents != null;
  return {
    year,
    prior: {
      year: year - 1,
      known,
      closingCents: known ? priorYearEndCents : null,
      drawnCents: priorDrawsCents,
      remainingCents: known ? priorYearEndCents - priorDrawsCents : null,
    },
    current: {
      year,
      retainedIncomeCents,
      drawnCents: thisYearDrawsCents,
      balanceCents: retainedIncomeCents - thisYearDrawsCents,
    },
    // What is left across both pots — the number the owner is really asking
    // about when they wonder how much they can take out.
    totalCents: (known ? priorYearEndCents - priorDrawsCents : 0)
      + retainedIncomeCents - thisYearDrawsCents,
  };
}
